import { distanceInMeters } from './geoDistance';
import { CATEGORY_ICONS } from '../trashcan/config/categoryIcons';
import { inspectRealWordPressLocation, repairWordPressDisplayText } from '../game-map/realLocations.mjs';
import { LOOT_RADIUS_METERS } from '../game-map/locationPolicy.mjs';

const DEFAULT_LOOT_RADIUS_M = LOOT_RADIUS_METERS;
const BOTTLE_MESSAGE_RADIUS_M = 30;
const STALE_DONATION_THRESHOLD_MS = 24 * 60 * 60 * 1000;
const CLOSED_DONATION_STATUSES = new Set(['abgeholt', 'picked_up', 'closed', 'done', 'abgelaufen', 'expired']);
const ACTIVE_DONATION_STATUSES = new Set(['offen', 'open', 'gemeldet', 'active']);

const CATEGORY_ALIASES = {
  'mülleimer': 'muelleimer',
  'muelleimer': 'muelleimer',
  'müllcontainer': 'muellcontainer',
  'muellcontainer': 'muellcontainer',
  'supermarkt': 'supermarkt',
  'pfandrückgabeautomat': 'pfandrueckgabeautomat',
  'pfandrueckgabeautomat': 'pfandrueckgabeautomat',
  'bushaltestelle': 'bushaltestelle',
  'markanter ort': 'markanter_ort',
  'sehenswürdigkeit': 'sehenswuerdigkeit',
  'sehenswuerdigkeit': 'sehenswuerdigkeit',
  'sehenswüdigkeit': 'sehenswuerdigkeit',
  'sehenswuedigkeit': 'sehenswuerdigkeit',
  'parkbank': 'parkbank',
  'pfandspende': 'pfandspende',
  'zu verschenken': 'giveaway',
  'zu_verschenken': 'giveaway',
  'fundsache': 'giveaway',
  'flaschenpost': 'flaschenpost',
};

export const TREASURE_MAP_CATEGORY_META = {
  muelleimer: {
    key: 'muelleimer',
    label: 'Mülleimer',
    iconUrl: CATEGORY_ICONS['Mülleimer'] || '',
    sourceType: 'location',
  },
  muellcontainer: {
    key: 'muellcontainer',
    label: 'Müllcontainer',
    iconUrl: CATEGORY_ICONS['Müllcontainer'] || CATEGORY_ICONS['Mülleimer'] || '',
    sourceType: 'location',
  },
  supermarkt: {
    key: 'supermarkt',
    label: 'Supermarkt',
    iconUrl: CATEGORY_ICONS['Supermarkt'] || '',
    sourceType: 'location',
  },
  pfandrueckgabeautomat: {
    key: 'pfandrueckgabeautomat',
    label: 'Pfandrückgabeautomat',
    iconUrl: CATEGORY_ICONS['Pfandrückgabeautomat'] || '',
    sourceType: 'location',
  },
  bushaltestelle: {
    key: 'bushaltestelle',
    label: 'Bushaltestelle',
    iconUrl: CATEGORY_ICONS['Bushaltestelle'] || '',
    sourceType: 'location',
  },
  markanter_ort: {
    key: 'markanter_ort',
    label: 'Markanter Ort',
    iconUrl: CATEGORY_ICONS['Markanter Ort'] || '',
    sourceType: 'location',
  },
  sehenswuerdigkeit: {
    key: 'sehenswuerdigkeit',
    label: 'Sehenswürdigkeit',
    iconUrl: CATEGORY_ICONS['Sehenswürdigkeit'] || '',
    sourceType: 'location',
  },
  parkbank: {
    key: 'parkbank',
    label: 'Parkbank',
    iconUrl: CATEGORY_ICONS['Parkbank'] || '',
    sourceType: 'location',
  },
  pfandspende: {
    key: 'pfandspende',
    label: 'Pfandspende',
    iconUrl: CATEGORY_ICONS.Pfandspende || '',
    sourceType: 'donation',
  },
  giveaway: {
    key: 'giveaway',
    label: 'Zu verschenken',
    iconUrl: CATEGORY_ICONS['Zu verschenken'] || '',
    sourceType: 'donation',
  },
  flaschenpost: {
    key: 'flaschenpost',
    label: 'Flaschenpost',
    iconUrl: '',
    sourceType: 'bottle_message',
  },
  sonstiges: {
    key: 'sonstiges',
    label: 'Sonstiger Ort',
    iconUrl: '',
    sourceType: 'location',
  },
};

function toNumber(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function parseDateMs(value) {
  if (!value) {
    return null;
  }

  const date = new Date(String(value).replace(' ', 'T'));
  const timestamp = date.getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
}

function normalizeText(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss');
}

function resolveRawTitle(raw = {}) {
  return raw.title?.rendered ?? raw.title ?? raw.name ?? '';
}

function resolveLocationDisplayTitle(raw = {}) {
  return repairWordPressDisplayText(resolveRawTitle(raw));
}

function resolveRawDescription(raw = {}) {
  return (
    raw.location_desc ??
    raw.description ??
    raw.desc ??
    raw.beschreibung ??
    raw.excerpt?.rendered ??
    raw.acf?.beschreibung ??
    raw.message ??
    ''
  );
}

function resolveRawLatitude(raw = {}) {
  return toNumber(
    raw.latitude ??
      raw.lat ??
      raw.acf?.latitude ??
      raw.acf?.lat ??
      raw.meta?.latitude ??
      raw.meta?.lat
  );
}

function resolveRawLongitude(raw = {}) {
  return toNumber(
    raw.longitude ??
      raw.lng ??
      raw.lon ??
      raw.acf?.longitude ??
      raw.acf?.lng ??
      raw.acf?.lon ??
      raw.meta?.longitude ??
      raw.meta?.lng ??
      raw.meta?.lon
  );
}

function ensureCategoryArray(value) {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }

  if (value === undefined || value === null || value === '') {
    return [];
  }

  return [value];
}

function resolveRawCategories(raw = {}) {
  return ensureCategoryArray(
    raw.categories ?? raw.category ?? raw['location-categories'] ?? raw.acf?.categories
  );
}

function resolveCategoryName(category) {
  if (!category) {
    return '';
  }

  if (typeof category === 'string') {
    return category;
  }

  return category.name ?? category.label ?? category.slug ?? '';
}


export function buildTreasureMapItemKey(item = {}) {
  return `${item?.sourceType || 'item'}-${item?.id ?? 'unknown'}`;
}

export function resolveTreasureMapCategory(categoryValue, fallbackKey = 'sonstiges') {
  const rawName = resolveCategoryName(categoryValue);
  const normalizedKey = CATEGORY_ALIASES[normalizeText(rawName)] || fallbackKey;
  const meta = TREASURE_MAP_CATEGORY_META[normalizedKey] || TREASURE_MAP_CATEGORY_META.sonstiges;

  return {
    key: meta.key,
    label: meta.label,
    iconUrl: meta.iconUrl,
  };
}

function buildDistanceMeters(latitude, longitude, userPos) {
  if (!Array.isArray(userPos) || userPos.length !== 2) {
    return null;
  }

  const distance = distanceInMeters(
    { latitude, longitude },
    { latitude: userPos[0], longitude: userPos[1] }
  );

  return Number.isFinite(distance) ? distance : null;
}

function resolveDonationStatus(raw = {}) {
  const candidates = [
    raw?.acf?.status,
    raw?.meta?.status,
    raw?.status,
  ];

  return candidates
    .map((value) => String(value || '').trim().toLowerCase())
    .find((value) => CLOSED_DONATION_STATUSES.has(value) || ACTIVE_DONATION_STATUSES.has(value)) || '';
}

export function normalizeTreasureMapLocation(
  raw = {},
  { userPos = null, getCooldownTime = null, lootRadiusM = DEFAULT_LOOT_RADIUS_M } = {}
) {
  const coordinateInspection = inspectRealWordPressLocation(raw);
  const { latitude, longitude } = coordinateInspection;
  const categories = resolveRawCategories(raw);
  const primaryCategory = resolveTreasureMapCategory(categories[0] || raw.category, 'sonstiges');
  const distanceMeters = buildDistanceMeters(latitude, longitude, userPos);
  const cooldownTimeMs = typeof getCooldownTime === 'function' ? Math.max(0, Number(getCooldownTime(raw.id) || 0)) : 0;
  const isOsmSeed = raw.source === 'osm_seed' || raw.origin === 'openstreetmap' || raw.is_auto_generated === true || raw.is_auto_generated === 1 || raw.is_auto_generated === '1';
  const lootableNow =
    latitude !== null &&
    longitude !== null &&
    distanceMeters !== null &&
    distanceMeters <= lootRadiusM &&
    cooldownTimeMs <= 0;

  return {
    ...raw,
    id: coordinateInspection.id,
    sourceType: 'location',
    title: resolveLocationDisplayTitle(raw),
    name: resolveLocationDisplayTitle(raw),
    description: resolveRawDescription(raw),
    location_desc: raw.location_desc ?? resolveRawDescription(raw),
    latitude,
    longitude,
    lat: latitude,
    lng: longitude,
    categories: categories.map((entry) => resolveCategoryName(entry)).filter(Boolean),
    category: primaryCategory.label,
    categoryKey: primaryCategory.key,
    categoryLabel: primaryCategory.label,
    iconUrl: primaryCategory.iconUrl,
    source: raw.source || '',
    origin: raw.origin || '',
    confidence: raw.confidence || '',
    isAutoGenerated: isOsmSeed,
    is_auto_generated: raw.is_auto_generated,
    sourceLabel: raw.source_label || (isOsmSeed ? 'OpenStreetMap' : ''),
    lootLabel: raw.loot_label || (isOsmSeed ? 'Vorgeschlagener Loot-Ort' : ''),
    notice: raw.notice || (isOsmSeed ? 'Automatisch vorgeschlagen, kein bestätigter Pfandfund.' : ''),
    distanceMeters,
    cooldownTimeMs,
    lootableNow,
    coordinateValidation: coordinateInspection,
  };
}

export function normalizeTreasureMapDonation(raw = {}, { userPos = null } = {}) {
  const latitude = resolveRawLatitude(raw);
  const longitude = resolveRawLongitude(raw);
  const distanceMeters = buildDistanceMeters(latitude, longitude, userPos);
  const donationStatus = resolveDonationStatus(raw);
  const isGiveaway = raw.item_type === 'giveaway' || raw.pfand_type === 'zu_verschenken';
  const donationCategory = isGiveaway ? TREASURE_MAP_CATEGORY_META.giveaway : TREASURE_MAP_CATEGORY_META.pfandspende;
  const description =
    raw.beschreibung ?? raw.description ?? raw.acf?.beschreibung ?? raw.excerpt?.rendered ?? '';
  const link = raw.link ?? raw.acf?.link ?? raw.url ?? '';
  const imageUrl = raw.photo_url || raw.image_url || raw.media_url || '';
  const createdAt = raw.created_at || raw.date || raw.acf?.created_at || '';
  const expiresAt = raw.expires_at || raw.ablaufdatum || raw.acf?.ablaufdatum || '';
  const modifiedAt = raw.modified_at || raw.modified || raw.acf?.modified_at || '';
  const lastConfirmedAt = raw.last_confirmed_at || raw.acf?.last_confirmed_at || '';
  const createdAtMs = parseDateMs(createdAt);
  const expiresAtMs = parseDateMs(expiresAt);
  const modifiedAtMs = parseDateMs(modifiedAt);
  const lastConfirmedAtMs = parseDateMs(lastConfirmedAt);
  const activityAtMs = Math.max(createdAtMs || 0, modifiedAtMs || 0, lastConfirmedAtMs || 0) || null;
  const isOlderThan24h = createdAtMs ? Date.now() - createdAtMs >= STALE_DONATION_THRESHOLD_MS : false;
  const glassCount = Math.max(0, Number.parseInt(raw.glass_count ?? raw.glassCount ?? 0, 10) || 0);
  const petReusableCount = Math.max(0, Number.parseInt(raw.pet_reusable_count ?? raw.petReusableCount ?? 0, 10) || 0);
  const oneWayCount = Math.max(0, Number.parseInt(raw.one_way_count ?? raw.oneWayCount ?? 0, 10) || 0);
  const exactTotalCount = glassCount + petReusableCount + oneWayCount;
  const totalCount = exactTotalCount || Math.max(0, Number.parseInt(raw.total_count ?? raw.quantity_score ?? 0, 10) || 0);
  const totalCents = exactTotalCount ? (glassCount * 8) + (petReusableCount * 15) + (oneWayCount * 25) : Math.max(0, Number.parseInt(raw.total_cents ?? 0, 10) || 0);

  return {
    ...raw,
    id: raw.id ?? raw.ID ?? null,
    sourceType: 'donation',
    title: resolveRawTitle(raw),
    name: resolveRawTitle(raw),
    description,
    beschreibung: description,
    imageUrl,
    image_url: imageUrl,
    photo_url: raw.photo_url || imageUrl,
    created_at: createdAt,
    expiresAt,
    expires_at: expiresAt,
    expiresAtMs,
    modified_at: modifiedAt,
    last_confirmed_at: lastConfirmedAt,
    createdAtMs,
    modifiedAtMs,
    lastConfirmedAtMs,
    activityAtMs,
    isOlderThan24h,
    isExpired: Boolean(expiresAtMs && expiresAtMs <= Date.now()),
    donationStatus,
    status: donationStatus || raw.status,
    latitude,
    longitude,
    lat: latitude,
    lng: longitude,
    categories: [donationCategory.label],
    category: donationCategory.label,
    categoryKey: donationCategory.key,
    categoryLabel: donationCategory.label,
    iconUrl: donationCategory.iconUrl,
    distanceMeters,
    lootableNow: false,
    cooldownTimeMs: 0,
    link,
    glassCount,
    petReusableCount,
    oneWayCount,
    totalCount,
    totalCents,
    accuracy: toNumber(raw.gps_accuracy ?? raw.accuracy),
  };
}

export function normalizeTreasureMapBottleMessage(
  raw = {},
  { userPos = null, openedState = null, lootRadiusM = BOTTLE_MESSAGE_RADIUS_M } = {}
) {
  const latitude = resolveRawLatitude(raw);
  const longitude = resolveRawLongitude(raw);
  const distanceMeters = buildDistanceMeters(latitude, longitude, userPos);
  const categoryMeta = TREASURE_MAP_CATEGORY_META.flaschenpost;
  const alreadyOpened = Boolean(raw?.has_opened || openedState?.success);
  const lootableNow =
    latitude !== null &&
    longitude !== null &&
    distanceMeters !== null &&
    distanceMeters <= lootRadiusM &&
    alreadyOpened === false;

  return {
    ...raw,
    id: raw.id ?? raw.ID ?? null,
    sourceType: 'bottle_message',
    title: raw?.title || 'Alte Flaschenpost',
    name: raw?.title || 'Alte Flaschenpost',
    description: resolveRawDescription(raw),
    latitude,
    longitude,
    lat: latitude,
    lng: longitude,
    categories: [categoryMeta.label],
    category: categoryMeta.label,
    categoryKey: categoryMeta.key,
    categoryLabel: categoryMeta.label,
    iconUrl: categoryMeta.iconUrl,
    distanceMeters,
    cooldownTimeMs: 0,
    lootableNow,
    openedState,
    lootRadiusM,
  };
}

export function normalizeTreasureMapBottleMessages(
  messages = [],
  { userPos = null, openedMessages = {}, lootRadiusM = BOTTLE_MESSAGE_RADIUS_M } = {}
) {
  return (Array.isArray(messages) ? messages : [])
    .map((entry) =>
      normalizeTreasureMapBottleMessage(entry, {
        userPos,
        openedState: openedMessages?.[entry?.id] || null,
        lootRadiusM,
      })
    )
    .filter((entry) => entry.latitude !== null && entry.longitude !== null);
}

export function buildTreasureMapCategories(locations = []) {
  const categories = new Map();

  (Array.isArray(locations) ? locations : []).forEach((location) => {
    const key = location?.categoryKey || resolveTreasureMapCategory(location?.category, 'sonstiges').key;
    const meta = TREASURE_MAP_CATEGORY_META[key] || TREASURE_MAP_CATEGORY_META.sonstiges;
    categories.set(meta.key, {
      key: meta.key,
      name: meta.label,
      label: meta.label,
      iconUrl: meta.iconUrl,
    });
  });

  return Array.from(categories.values()).sort((a, b) => a.label.localeCompare(b.label, 'de'));
}

export function normalizeTreasureMapDataset({
  locations = [],
  donations = [],
  userPos = null,
  getCooldownTime = null,
  lootRadiusM = DEFAULT_LOOT_RADIUS_M,
} = {}) {
  const locationResults = (Array.isArray(locations) ? locations : [])
    .map((entry) => ({
      raw: entry,
      normalized: normalizeTreasureMapLocation(entry, { userPos, getCooldownTime, lootRadiusM }),
    }));
  const normalizedLocations = locationResults
    .map((entry) => entry.normalized)
    .filter((entry) => entry.coordinateValidation?.valid === true);
  const invalidLocations = locationResults
    .filter((entry) => entry.normalized.coordinateValidation?.valid !== true)
    .map((entry) => ({
      id: entry.raw?.id ?? entry.raw?.ID ?? entry.raw?.post_id ?? entry.raw?.location_id ?? null,
      title: resolveRawTitle(entry.raw),
      reasons: entry.normalized.coordinateValidation?.reasons || ['invalid-location'],
    }));
  const categoryWarnings = locationResults.flatMap(({ raw }) => {
    const rawNames = resolveRawCategories(raw).map((entry) => resolveCategoryName(entry));
    return rawNames
      .filter((name) => normalizeText(name) === 'sehenswuedigkeit')
      .map((name) => ({ id: raw?.id ?? null, category: name, reason: 'misspelled-sehenswuerdigkeit' }));
  });

  const normalizedDonations = (Array.isArray(donations) ? donations : [])
    .map((entry) => normalizeTreasureMapDonation(entry, { userPos }))
    .filter((entry) => entry.latitude !== null && entry.longitude !== null && !entry.isExpired && !CLOSED_DONATION_STATUSES.has(entry.donationStatus))
    .sort((a, b) => (Number(b.activityAtMs || b.createdAtMs || 0) - Number(a.activityAtMs || a.createdAtMs || 0)));

  const items = [...normalizedLocations, ...normalizedDonations].sort((a, b) => {
    if (a.sourceType === 'donation' && b.sourceType === 'donation') {
      return Number(b.activityAtMs || b.createdAtMs || 0) - Number(a.activityAtMs || a.createdAtMs || 0);
    }

    const distanceA = Number.isFinite(a.distanceMeters) ? a.distanceMeters : Number.MAX_SAFE_INTEGER;
    const distanceB = Number.isFinite(b.distanceMeters) ? b.distanceMeters : Number.MAX_SAFE_INTEGER;
    return distanceA - distanceB;
  });

  return {
    locations: normalizedLocations,
    donations: normalizedDonations,
    items,
    categories: buildTreasureMapCategories(normalizedLocations),
    locationDiagnostics: {
      received: locationResults.length,
      valid: normalizedLocations.length,
      invalid: invalidLocations,
      categoryWarnings,
    },
  };
}
